import { Archive, ChefHat, MapPin, Package, Snowflake, Sun, Zap } from "lucide-react";
import { gruppoConservazione } from "./conservazione";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const [year, month, day] = dateStr.slice(0, 10).split("-");
  const suffix = year !== String(new Date().getFullYear()) ? `/${year}` : "";
  return `${day}/${month}${suffix}`;
};

/**
 * Configurazione visiva per stato lotto.
 * Card mobile-first con bordo + label colorate per identificare a colpo d'occhio.
 */
const STATO_STYLES = {
  frigo: {
    border: "border-l-emerald-400",
    bg: "bg-card",
    label: null,
  },
  temp_ambiente: {
    border: "border-l-amber-500",
    bg: "bg-amber-50/40 dark:bg-amber-500/5",
    label: { text: "TEMP. AMBIENTE", color: "bg-amber-500 text-white" },
  },
  congelato: {
    border: "border-l-sky-500",
    bg: "bg-sky-50/40 dark:bg-sky-500/5",
    label: { text: "CONGELATO", color: "bg-sky-500 text-white", Icon: Snowflake },
  },
  abbattuto: {
    border: "border-l-indigo-500",
    bg: "bg-indigo-50/40 dark:bg-indigo-500/5",
    label: { text: "ABBATTUTO", color: "bg-indigo-500 text-white", Icon: Zap },
  },
  scongelato: {
    border: "border-l-orange-500",
    bg: "bg-orange-50/40 dark:bg-orange-500/5",
    label: { text: "SCONGELATO", color: "bg-orange-500 text-white", Icon: Sun },
  },
};

const LottoCard = ({
  lotto,
  prodottoNome,
  posizioneNome,
  isSelected,
  onSelect,
  onClick,
  isArchived = false,
}) => {
  const getDaysUntil = (dataScadenza) => {
    if (!dataScadenza) return 999;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const scadenza = new Date(dataScadenza);
    scadenza.setHours(0, 0, 0, 0);
    return Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
  };

  const getProgressPercent = (dataCreazione, dataScadenza) => {
    if (!dataCreazione || !dataScadenza) return null;
    const start = new Date(dataCreazione);
    const end = new Date(dataScadenza);
    const today = new Date();
    const total = end - start;
    if (total <= 0) return 100;
    return Math.min(100, Math.max(0, ((today - start) / total) * 100));
  };

  const getProgressColor = (percent) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 75) return "bg-orange-500";
    if (percent >= 50) return "bg-amber-400";
    return "bg-emerald-500";
  };

  const dataScadenzaEffettiva = lotto.data_scadenza_aperto || lotto.data_scadenza;
  const hasScadenza = !!dataScadenzaEffettiva;
  const days = getDaysUntil(dataScadenzaEffettiva);
  const isExpired = hasScadenza && days < 0;
  const isPreparato = lotto.is_composto;
  const dataCreazione = lotto.data_apertura || lotto.created_at?.split("T")[0];
  const progressPercent = getProgressPercent(dataCreazione, dataScadenzaEffettiva);

  // Determina lo stato visivo (abbattuto vs congelato in base a metodo_congelamento)
  const statoVisivo = gruppoConservazione(lotto);
  const styleConfig = STATO_STYLES[statoVisivo] || STATO_STYLES.frigo;

  // Badge scadenza
  let badgeStyle = "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400";
  let badgeText = "OK";
  if (!hasScadenza) {
    badgeStyle = "bg-muted text-muted-foreground";
    badgeText = "N/D";
  } else if (isExpired) {
    badgeStyle = "bg-red-500/20 text-red-600 dark:text-red-400";
    badgeText = "SCADUTO";
  } else if (days === 0) {
    badgeStyle = "bg-amber-500/20 text-amber-700 dark:text-amber-400";
    badgeText = "OGGI";
  } else if (days === 1) {
    badgeStyle = "bg-amber-500/20 text-amber-700 dark:text-amber-400";
    badgeText = "DOMANI";
  } else if (days <= 7) {
    badgeStyle = "bg-amber-500/20 text-amber-700 dark:text-amber-400";
    badgeText = `${days}gg`;
  } else if (days <= 14) {
    badgeStyle = "bg-orange-500/15 text-orange-600 dark:text-orange-400";
    badgeText = `${days}gg`;
  }

  let ringClass = "";
  if (isArchived) ringClass = "opacity-60";
  else if (isSelected) ringClass = "ring-2 ring-lime-400";

  const StatoIcon = styleConfig.label?.Icon;

  return (
    <div
      onClick={onClick}
      data-testid={`lotto-card-${lotto.id}`}
      data-lotto-id={lotto.id}
      data-stato={statoVisivo}
      className={`p-3 rounded-xl border border-l-4 ${styleConfig.border} ${styleConfig.bg} ${ringClass} cursor-pointer transition-all active:scale-[0.99]`}
    >
      {/* Riga 1: Checkbox + Icona + Nome + Badge scadenza */}
      <div className="flex items-center gap-2 mb-1">
        {!isArchived ? (
          <div
            onClick={(e) => { e.stopPropagation(); onSelect && onSelect(lotto.id); }}
            className="flex items-center justify-center w-5 h-5 flex-shrink-0"
            data-testid={`lotto-select-${lotto.id}`}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
              isSelected ? 'bg-lime-400 border-lime-400' : 'border-muted-foreground/30'
            }`}>
              {isSelected && (
                <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
        ) : (
          <Archive className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}

        {isPreparato
          ? <ChefHat className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          : <Package className="h-3.5 w-3.5 text-lime-500 flex-shrink-0" />
        }

        <span className={`flex-1 font-semibold text-sm truncate ${isArchived ? 'text-muted-foreground' : 'text-foreground'}`}>
          {prodottoNome}
        </span>

        <span className={`flex-shrink-0 px-2 py-0.5 rounded-md text-xs font-bold ${badgeStyle} ${isArchived ? 'opacity-50' : ''}`}>
          {badgeText}
        </span>
      </div>

      {/* Numero lotto sotto al nome */}
      {lotto.numero_lotto && (
        <div className="ml-7 mb-1 -mt-0.5">
          <span className="text-[11px] font-mono font-semibold text-muted-foreground" data-testid={`lotto-numero-${lotto.id}`}>
            Lotto {lotto.numero_lotto}
          </span>
        </div>
      )}

      {/* Riga 2: Badge stato HACCP (solo se non fresco) — pillola subito sotto il nome */}
      {styleConfig.label && (
        <div className="ml-7 mb-1.5 flex items-center">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide ${styleConfig.label.color}`} data-testid={`lotto-conservazione-badge-${lotto.id}`}>
            {StatoIcon && <StatoIcon className="h-2.5 w-2.5" />}
            {styleConfig.label.text}
          </span>
        </div>
      )}

      {/* Riga 3: Dati allineati — Creato | Scade | Peso */}
      <div className="flex items-center gap-3 text-xs ml-7 mb-2">
        <span className="text-muted-foreground">
          Creato <span className="text-foreground font-medium">{formatDate(dataCreazione)}</span>
        </span>
        {hasScadenza && (
          <span className="text-muted-foreground">
            Scade <span className={`font-medium ${isExpired ? 'text-red-500' : days <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
              {formatDate(dataScadenzaEffettiva)}
            </span>
          </span>
        )}
        {(() => {
          const qp = lotto.quantita_preparata;
          const qi = lotto.quantita_iniziale;
          const um = lotto.unita_misura || "";
          if (qp != null && qp !== "") {
            const showInitial = qi != null && qi !== "" && Number(qi) !== Number(qp);
            return (
              <span className="ml-auto text-foreground font-medium" data-testid={`lotto-qta-${lotto.id}`}>
                {showInitial ? `${qp}/${qi}` : qp}{um ? ` ${um}` : ""}
              </span>
            );
          }
          return lotto.quantita_testo ? (
            <span className="ml-auto text-foreground font-medium">{lotto.quantita_testo}</span>
          ) : null;
        })()}
      </div>

      {posizioneNome && (
        <div className="ml-7 mb-2 inline-flex max-w-[calc(100%-1.75rem)] items-center gap-1 text-xs text-muted-foreground" data-testid={`lotto-posizione-${lotto.id}`}>
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{posizioneNome}</span>
        </div>
      )}

      {/* Riga 4: Barra di progresso temporale */}
      {progressPercent !== null && !isArchived && (
        <div className="ml-7 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full ${getProgressColor(progressPercent)}`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default LottoCard;
